<template>
    <div v-if="showBanner && hasWhatsappV3Channel && !isExpired" class="whatsapp-v3-warning-wrapper">
        <transition name="slide-down">
            <div class="whatsapp-v3-warning-banner" v-if="bannerVisible">
                <div class="banner-main">
                    <div class="banner-content" @click="showDetailModal = true">
                        <span class="material-icons warning-icon">warning</span>
                        <span class="banner-text">
                            {{ $t("message.whatsappV3BannerWarning") }}
                        </span>
                    </div>
                    <button class="close-btn" @click.stop="closeBanner">
                        <span class="material-icons">close</span>
                    </button>
                </div>
                <div class="progress-bar-container">
                    <div class="progress-bar" :style="{ width: progressWidth + '%' }"></div>
                </div>
            </div>
        </transition>

        <a-modal
            :visible="showDetailModal"
            :footer="null"
            :closable="true"
            @cancel="closeDetailModal"
            width="600px"
            class="whatsapp-v3-detail-modal"
            centered
        >
            <template #title>
                <div class="modal-title">
                    <span class="material-icons" style="color: #f5a623; margin-right: 8px;">warning</span>
                    {{ $t("message.whatsappV3ModalTitle") }}
                </div>
            </template>
            <div class="modal-content">
                <div class="warning-section">
                    <div class="section-header">
                        <span class="material-icons">error_outline</span>
                        <h3>{{ $t("message.whatsappV3ServiceStatusHeading") }}</h3>
                    </div>
                    <p>{{ $t("message.whatsappV3ServiceStatusP1") }}</p>
                    <p>{{ $t("message.whatsappV3ServiceStatusP2") }}</p>
                </div>

                <div class="info-section">
                    <div class="section-header">
                        <span class="material-icons">info</span>
                        <h3>{{ $t("message.whatsappV3WaIdHeading") }}</h3>
                    </div>
                    <p>{{ $t("message.whatsappV3WaIdP1") }}</p>
                    <p class="highlight-text">{{ $t("message.whatsappV3WaIdP2") }}</p>
                </div>

                <div class="action-section">
                    <a-button type="primary" @click="goToChannelSettings">
                        <span class="material-icons" style="font-size: 18px; margin-right: 4px;">settings</span>
                        {{ $t("message.whatsappV3GoChannelSettings") }}
                    </a-button>
                    <a-button @click="closeDetailModal">
                        {{ $t("message.whatsappV3Understood") }}
                    </a-button>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    name: "WhatsappV3WarningBanner",
    data() {
        return {
            showBanner: true,
            bannerVisible: true,
            showDetailModal: false,
            autoHideTimer: null,
            progressTimer: null,
            progressWidth: 100,
            totalDuration: 60000, // 60 saniye
            intervalStep: 100, // 100ms'de bir güncelle
        };
    },
    computed: {
        ...mapState({
            hasWhatsappV3Channel: (state) => state.channel.channels.some((x) => x.type === "whatsapp-unipile"),
        }),
        isExpired() {
            // 26.12.2025 00:00 UTC+3 (Türkiye saati)
            const expiryDate = new Date("2025-12-29T00:00:00+03:00");
            return Date.now() >= expiryDate.getTime();
        },
    },
    methods: {
        closeBanner() {
            this.bannerVisible = false;
            this.clearTimers();
        },
        closeDetailModal() {
            this.showDetailModal = false;
            this.bannerVisible = false;
            this.clearTimers();
        },
        goToChannelSettings() {
            this.showDetailModal = false;
            this.bannerVisible = false;
            this.$router.push("/integrations/whatsapp-v3");
        },
        startAutoHideTimer() {
            this.clearTimers();
            this.progressWidth = 100;
            const decrementPerStep = (this.intervalStep / this.totalDuration) * 100;
            this.progressTimer = setInterval(() => {
                this.progressWidth -= decrementPerStep;
                if (this.progressWidth <= 0) {
                    this.progressWidth = 0;
                    this.bannerVisible = false;
                    this.clearTimers();
                }
            }, this.intervalStep);
        },
        clearTimers() {
            if (this.autoHideTimer) {
                clearTimeout(this.autoHideTimer);
                this.autoHideTimer = null;
            }
            if (this.progressTimer) {
                clearInterval(this.progressTimer);
                this.progressTimer = null;
            }
        },
    },
    watch: {
        hasWhatsappV3Channel: {
            handler(val) {
                if (val && !this.isExpired) {
                    this.bannerVisible = true;
                    this.startAutoHideTimer();
                }
            },
            immediate: true,
        },
    },
    mounted() {
        if (this.hasWhatsappV3Channel && !this.isExpired) {
            this.startAutoHideTimer();
        }
    },
    beforeDestroy() {
        this.clearTimers();
    },
};
</script>

<style lang="scss" scoped>
.whatsapp-v3-warning-wrapper {
    position: fixed;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
}

.whatsapp-v3-warning-banner {
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
    border: 1px solid #ffc107;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    overflow: hidden;

    .banner-main {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 16px;
    }

    .banner-content {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.8;
        }

        .warning-icon {
            color: #f5a623;
            font-size: 20px;
        }

        .banner-text {
            color: #856404;
            font-size: 14px;
            font-weight: 500;
        }
    }

    .close-btn {
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background 0.2s;

        &:hover {
            background: rgba(0, 0, 0, 0.1);
        }

        .material-icons {
            font-size: 18px;
            color: #856404;
        }
    }

    .progress-bar-container {
        width: 100%;
        height: 3px;
        background: rgba(0, 0, 0, 0.1);

        .progress-bar {
            height: 100%;
            background: linear-gradient(90deg, #f5a623, #ff9800);
            transition: width 0.1s linear;
        }
    }
}


.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}

.modal-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
}

.modal-content {
    .warning-section,
    .info-section {
        background: #f8f9fa;
        border-radius: 8px;
        padding: 16px;
        margin-bottom: 16px;

        .section-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;

            .material-icons {
                font-size: 22px;
            }

            h3 {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
            }
        }

        p {
            margin: 0 0 8px 0;
            color: #495057;
            line-height: 1.6;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    .warning-section {
        border-left: 4px solid #f5a623;

        .section-header .material-icons {
            color: #f5a623;
        }
    }

    .info-section {
        border-left: 4px solid #17a2b8;

        .section-header .material-icons {
            color: #17a2b8;
        }

        .highlight-text {
            background: #fff3cd;
            padding: 10px 12px;
            border-radius: 6px;
            font-weight: 500;
            color: #856404;
        }
    }

    .action-section {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 20px;
        padding-top: 16px;
        border-top: 1px solid #e9ecef;
    }
}
</style>

